import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  Container,
  Row,
  Col,
  Card,
  Alert,
  Button,
  Spinner,
  Navbar
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import authService from '../../services/authService';
import ProgressBar from './ProgressBar';
import PersonalInfo from './FormSections/PersonalInfo';
import ContactInfo from './FormSections/ContactInfo';
import AddressInfo from './FormSections/AddressInfo';
import InsuranceInfo from './FormSections/InsuranceInfo';
import TermsAndConditions from './FormSections/TermsAndConditions';

const FormLayout = () => {
  const [formData, setFormData] = useState({
    provider: {
      provider_id: '360968643'
    },
    patient: {
      bio: {
        gender: '',
        date_of_birth: '',
        phone_number: '',
        home_phone_number: '',
        cell_phone_number: '',
        work_phone_number: '',
        custom_contact_number: '',
        weight: '',
        height: '',
        street_address: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        zip_code: '',
        insurance_name: '',
        ssn: '',
        race: ''
      },
      first_name: '',
      last_name: '',
      email: ''
    }
  });

  const [providers, setProviders] = useState([]);
  const [useHardcodedProvider, setUseHardcodedProvider] = useState(true);
  const [hardcodedProviderId] = useState('360968643');
  const [submitted, setSubmitted] = useState(false);
  const [expired, setExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [error, setError] = useState('');
  const [formSection, setFormSection] = useState('personal');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { token } = useParams();

  // Session timeout for security
  const [, setSessionTimeout] = useState(null);
  const SESSION_DURATION = 20 * 60 * 1000; // 20 minutes

  const resetSessionTimeout = useCallback(() => {
    setSessionTimeout(prevTimeout => {
      if (prevTimeout) clearTimeout(prevTimeout);
      return setTimeout(() => {
        if (!submitted) {
          setExpired(true);
          toast.error('Your session has expired due to inactivity');
        }
      }, SESSION_DURATION);
    });
  }, [submitted, SESSION_DURATION]);

  useEffect(() => {
    resetSessionTimeout();
    
    // Add event listeners to reset timeout on user activity
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
    const handleActivity = () => resetSessionTimeout();
    
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });
    
    return () => {
      setSessionTimeout(prevTimeout => {
        if (prevTimeout) clearTimeout(prevTimeout);
        return null;
      });
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    }
  }, [resetSessionTimeout]);

  useEffect(() => {
    const checkTokenValidity = async () => {
      try {
        const response = await authService.validatePatientFormToken(token);
        if (!response.data.valid) {
          setExpired(true);
          toast.error('This form link has expired or is invalid');
        }
      } catch (err) {
        setExpired(true);
        toast.error('Error validating form link');
      }
    };

    checkTokenValidity();
    fetchProviders(token);
  }, [token]);

  const fetchProviders = async (token) => {
    setLoadingProviders(true);
    try {
      const response = await authService.getNexhealthProviders(token);
      if (response.code) {
        setProviders(response.data);
      }
    } catch (err) {
      setError('Failed to load providers');
      toast.error('Failed to load provider list');
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('bio.')) {
      const bioField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        patient: {
          ...prev.patient,
          bio: {
            ...prev.patient.bio,
            [bioField]: value
          }
        }
      }));
    } else if (name === 'provider_id') {
      setFormData(prev => ({
        ...prev,
        provider: {
          provider_id: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        patient: {
          ...prev.patient,
          [name]: value
        }
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Create a copy of the form data for submission
    const submissionData = JSON.parse(JSON.stringify(formData));
    
    // Set gender to "Other" if it's empty
    if (!submissionData.patient.bio.gender) {
      submissionData.patient.bio.gender = "Other";
    }
    
    if (!submissionData.patient.first_name || !submissionData.patient.last_name || 
        !submissionData.patient.email || !submissionData.patient.bio.date_of_birth || 
        !submissionData.patient.bio.phone_number) {
      setError('Please fill in all required fields (marked with *)');
      setLoading(false);
      toast.error('Please fill in all required fields');
      return;
    }

    if (!acceptTerms) {
      setError('You must accept the Terms and Conditions to proceed');
      setLoading(false);
      toast.error('Please accept the Terms and Conditions');
      return;
    }

    try {
      const finalProviderId = useHardcodedProvider 
        ? (providers.length > 0 ? providers[0].id : hardcodedProviderId)
        : formData.provider.provider_id;
      
      const response = await authService.registerNexhealthPatient(token, {
        ...submissionData,
        provider: {
          provider_id: finalProviderId
        }
      });
      
      if (response.code) {
        setSubmitted(true);
        await authService.inValidatePatientFormToken(token);
        // console.log(`Invalidate response :: ${response}`);
        toast.success('Information submitted successfully!');
      } else {
        setError(response.error || 'Failed to submit form');
        toast.error(response.error || 'Submission failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
      toast.error(err.response?.data?.error || 'Submission error');
    } finally {
      setLoading(false);
    }
  };

  const goToNextSection = (e) => {
    e.preventDefault();
    const sections = ['personal', 'contact', 'address', 'insurance'];
    const currentIndex = sections.indexOf(formSection);
    if (currentIndex < sections.length - 1) {
      setFormSection(sections[currentIndex + 1]);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousSection = (e) => {
    e.preventDefault();
    const sections = ['personal', 'contact', 'address', 'insurance'];
    const currentIndex = sections.indexOf(formSection);
    if (currentIndex > 0) {
      setFormSection(sections[currentIndex - 1]);
      window.scrollTo(0, 0);
    }
  };

  if (expired) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center py-5">
        <Card className="mx-auto" style={{ maxWidth: '450px' }}>
          <Card.Body className="text-center py-5 bg-light">
            <div className="mb-4 text-danger">
              {/* Expired icon */}
            </div>
            <h2 className="mb-2 fw-bold">Session Expired</h2>
            <p className="text-muted">This form link has expired or is invalid for security purposes.</p>
            <p className="small">For your security, please request a new form link.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (submitted) {
    return (
      <Container className="min-vh-100 d-flex align-items-center justify-content-center py-5">
        <Card className="mx-auto" style={{ maxWidth: '450px' }}>
          <Card.Body className="text-center py-5 bg-light">
            <div className="mb-4 text-success">
              {/* Success icon */}
            </div>
            <h2 className="mb-2 fw-bold">Thank You!</h2>
            <p className="text-muted">Your information has been successfully submitted.</p>
            <p className="small">Your data is securely stored in accordance with HIPAA guidelines.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={8} xl={7}>
          <div className="text-center mb-4">
            <Navbar.Brand className="purple-color righteousy-font" as={Link} to="/">
              <span className="fs-2 fw-bold text-dark">Nouraa</span>
              <span className="fs-3 fw-light text-primary">.ai</span>
            </Navbar.Brand>
          </div>
          
          <Card className="shadow-sm mb-4">
            <Card.Body className="p-4 p-md-5">
              <h2 className="text-center fw-bold mb-4">Patient Information Form</h2>
              
              {error && (
                <Alert variant="danger" className="mb-4">
                  <div className="d-flex align-items-center">
                    {error}
                  </div>
                </Alert>
              )}
              
              <div className="mb-4">
                <Alert variant="info">
                  <div className="d-flex">
                    <div className="me-3">
                      <i className="bi bi-shield-lock fs-4"></i>
                    </div>
                    <div>
                      <strong>HIPAA Compliant Form</strong>
                      <p className="mb-0 small">This secure form is HIPAA compliant. Your information is encrypted and only accessible to authorized healthcare personnel.</p>
                    </div>
                  </div>
                </Alert>
              </div>
              
              <ProgressBar formSection={formSection} />
              
              <form>
                {formSection === 'personal' && (
                  <PersonalInfo formData={formData} handleChange={handleChange} />
                )}
                {formSection === 'contact' && (
                  <ContactInfo formData={formData} handleChange={handleChange} />
                )}
                {formSection === 'address' && (
                  <AddressInfo formData={formData} handleChange={handleChange} />
                )}
                {formSection === 'insurance' && (
                  <>
                    <InsuranceInfo 
                      formData={formData} 
                      handleChange={handleChange}
                      providers={providers}
                      useHardcodedProvider={useHardcodedProvider}
                      setUseHardcodedProvider={setUseHardcodedProvider}
                      loadingProviders={loadingProviders}
                    />
                    
                    <TermsAndConditions 
                      acceptTerms={acceptTerms} 
                      setAcceptTerms={setAcceptTerms} 
                    />
                  </>
                )}

                <div className="d-flex justify-content-between mt-4">
                  {formSection !== 'personal' && (
                    <Button 
                      variant="outline-secondary" 
                      onClick={goToPreviousSection}
                      type="button"
                    >
                      Back
                    </Button>
                  )}
                  
                  {formSection !== 'insurance' ? (
                    <Button 
                      variant="primary" 
                      onClick={goToNextSection}
                      type="button"
                      className={formSection === 'personal' ? 'w-100' : 'ms-auto'}
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      variant="primary"
                      onClick={handleSubmit}
                      disabled={loading || !acceptTerms}
                      className="ms-auto"
                    >
                      {loading ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                          Submitting...
                        </>
                      ) : 'Submit'}
                    </Button>
                  )}
                </div>
              </form>
            </Card.Body>
            <Card.Footer className="bg-light py-3">
              <small className="text-muted d-block text-center">
                Fields marked with <span className="text-danger">*</span> are required. Please ensure all information is accurate and complete for proper medical record keeping in accordance with HIPAA guidelines. The information provided will be securely stored and only accessed by authorized healthcare personnel.
              </small>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default FormLayout;